import { push, replace } from "connected-react-router";
import { stringify } from "query-string";

import * as probes from "./probes_action";
import ApiService from "../services/ApiService";
import * as types from "./ActionTypes";

import TagService from "../services/TagService";
import { path } from "../conf/route.conf";
import { screens } from "../conf/tags_ga.conf";

import { createIdentities } from '../actions/LoginActions';

const requestAccess = ({ qs }) => ({
  type: types.REQUEST_ACCESS,
  payload: {
    qs
  }
});

const receiveAccessNewSession = ({ login, srv, act }) => ({
  type: types.RECEIVE_ACCESS_NEW_SESSION,
  payload: {
    login,
    srv,
    act,
  }
});

const receiveAccessByPass = ({ identitiesId, lostId, srv, act }) => ({
  type: types.RECEIVE_ACCESS_BYPASS,
  payload: {
    identitiesId,
    lostId,
    srv,
    act,
  }
});

export const setWebViewMode = isEnabled => ({
  type: types.SET_WEBVIEW_MODE,
  payload: {
    isEnabled
  }
});

const receiveAccessFailure = () => ({
  type: types.RECEIVE_ACCESS_FAILURE
});

export const setErrorPage = error => ({
  type: types.SET_ERROR_PAGE,
  payload: {
    error
  }
});

export const addWhiteListPage = step => ({
  type: types.ADD_WHITELIST_PAGE,
  payload: {
    step: `/${step}`
  }
});

export const deleteWhiteListPage = step => ({
  type: types.DELETE_WHITELIST_PAGE,
  payload: {
    step
  }
});

const requestRemoveAccess = () => ({
  type: types.REQUEST_REMOVE_ACCESS
});

const removeAccessSuccessful = qs => ({
  type: types.REMOVE_ACCESS_SUCCESS,
  payload: {
    qs
  }
});

const removeAccessFailure = error => ({
  type: types.REMOVE_ACCESS_FAILURE,
  error
});

export const resetStore = () => ({
  type: types.RESET_STORE
});

const requestCredentials = login => ({
  type: types.CHECK_CREDENTIALS,
  payload: {
    login
  }
});

const receiveCredentialsSuccess = () => ({
  type: types.CHECK_CREDENTIALS_SUCCESS
});

const receiveCredentialsError = error => ({
  type: types.CHECK_CREDENTIALS_FAILURE,
  error
});

export const setInputError = ({ field, error }) => ({
  type: types.SET_INPUT_ERROR,
  payload: {
    field,
    error
  }
});

export const clearInputError = ({ field }) => ({
  type: types.CLEAR_INPUT_ERROR,
  payload: {
    field
  }
});

export const clearWhiteListPages = () => ({
  type: types.CLEAR_WHITELIST_PAGES
});

const requestIdentities = identitiesId => ({
  type: types.GET_IDENTITIES,
  identitiesId,
});

const getIdentitiesSuccess = identities => ({
  type: types.GET_IDENTITIES_SUCCESS,
  payload: {
    identities,
  },
});

const getIdentitiesFailure = error => ({
  type: types.GET_IDENTITIES_FAILURE,
  error,
});

/**
 * Generic action to go to the next step
 * Add to whitelist the step received by server before push to it
 * Sending some probes to the server
 * @param step
 * @param action
 * @return {const(*, *): Promise<void>}
 */
export const goToNextStep = (step, action = "push") => dispatch => {
  // Sending probes
  dispatch(probes.sendingProbes());

  // Add the next step to whitelist
  dispatch(addWhiteListPage(step));

  switch (action) {
    case "replace":
      dispatch(replace(`/${step}`));
      break;
    default:
      dispatch(push(`/${step}`));
      break;
  }

  // Send the screen to Google Analytics
  TagService.sendScreen(step);
  TagService.setDataLayer("name", screens[step]);
};

/**
 * Go to the error page with the specific error message
 * @param error
 * @return {Function}
 */
export const goToErrorPage = error => dispatch => {
  // Set the specific error page if exists
  if (error) {
    dispatch(setErrorPage(error));
  } else {
    TagService.sendAction("technicalError");
    dispatch(setErrorPage("technicalError"));
  }

  // Push to error page
  dispatch(push(path.ERROR));

  // Send the screen to Google Analytics
  TagService.sendScreen("error");
};

/**
 * Get principal user informations
 * @param {string} identitiesId  String user id
 */
export const getIdentities = identitiesId => ((dispatch) => {
  dispatch(requestIdentities(identitiesId));
  return ApiService.getIdentities({ identitiesId }).then(
    (res) => {
      const { data } = res.data;
      dispatch(getIdentitiesSuccess(data));
    },
    (err) => {
      dispatch(getIdentitiesFailure(err.response));
      dispatch(goToErrorPage());
    },
  );
});

/**
 * Thunk action to request access
 * When a session was created by challenges call partners, bypass to fourth step
 * When a new session is created, go to first step
 * When an error occured, display the correct error page
 * @return {const(*): *}
 */
export const getAccess = ({ qs, device }) => dispatch => {
  // Define lost-id token if exists in query-string
  if (qs && qs.lostid) {
    ApiService.lostId = qs.lostid;
  }

  dispatch(probes.recordingProbeStarted());
  dispatch(requestAccess({ qs }));
  return ApiService.postAccess({ qs, device }).then(
    async res => {
      const { data, step } = res.data;
      const { identitiesId, lostId, login, srv, act } = data;
      switch (res.status) {
        case 201: {
          if (login && step === 'choice') {
            /*
            * ATTENTION! This call must do only in await, not remove it
            */
            await dispatch(createIdentities({ login, captcha: '-', nextStep: false }));

          }
          dispatch(receiveAccessNewSession({ login, srv, act }));
          break;
        }
        case 200: {
          await dispatch(getIdentities(identitiesId));
          dispatch(receiveAccessByPass({ identitiesId, lostId, srv, act }));
          break;
        }
        default:
          break;
      }
      dispatch(goToNextStep(step, "replace"));
    },
    err => {
      dispatch(receiveAccessFailure(err.response));
      if (err.response && err.response.data) {
        const { code } = err.response.data;
        switch (err.response.status) {
          case 403:
            if (code === 403004) dispatch(goToErrorPage("geolocalisation"));
            else dispatch(goToErrorPage("blacklisted"));
            break;
          case 412:
            window.location.href = "/";
            break;
          default:
            if (code === 500401) dispatch(goToErrorPage("rtcTechnicalError"));
            else dispatch(goToErrorPage("technicalError"));
            break;
        }
      }
    }
  );
};

export const checkCredentials = (value) => dispatch => {
  dispatch(requestCredentials(value));
  return ApiService.checkCredentials({ login: value }).then(
    () => {
      dispatch(receiveCredentialsSuccess());
    },
    err => {
      dispatch(receiveCredentialsError(err.response));
      if (err.response && err.response.data) {
        const { code } = err.response.data;
        switch (code) {
          case 412002:
            dispatch(setInputError({ field: 'login', error: "empty" }));
            break;
          case 412004:
            dispatch(setInputError({ field: 'login', error: "syntaxInvalid" }));
            break;
          default:
            break;
        }
      }
    }
  );
};

/**
 * Action to call '/access/me' and delete the current session server
 * @return {const(*): Promise<T>}
 */
export const removeAccess = () => dispatch => {
  dispatch(requestRemoveAccess());
  return ApiService.removeAccess().then(
    res => {
      const { data } = res.data;
      dispatch(removeAccessSuccessful(data));
    },
    err => dispatch(removeAccessFailure(err.response))
  );
};

/**
 * Quit the app with the redirect url received by server
 * @return {Function}
 */
export const quitApp = () => (dispatch, getState) => {
  const { webViewMode, serviceConf } = getState().generic;

  // Remove access server
  dispatch(removeAccess()).then(() => {
    const { redirectUrl } = getState().generic.qs;

    let url = serviceConf.confirmButtonUrl || redirectUrl || window.reactAppEnv.orangeAuth;

    if (webViewMode) {
      const qs = stringify({ webview_action: "closewebview" });
      url += (url.match(/\?/) ? "&" : "?") + qs;
    }

    // Exit the app
    window.location.href = url;
  });
};
